import React from "react";
import { Card, Button, Badge, Spinner, Table } from "react-bootstrap";
import Swal from "sweetalert2";
import {
  useFetchBackupsQuery,
  useCreateBackupMutation,
  useDeleteBackupMutation,
} from "features/backups/backupsSlice";
import { useSelector } from "react-redux";
import { RootState } from "app/store";

const BackupsTab: React.FC = () => {
  const { data: backups = [], isLoading } = useFetchBackupsQuery();
  const [createBackup, { isLoading: creating }] = useCreateBackupMutation();
  const [deleteBackup] = useDeleteBackupMutation();
  const token = useSelector((state: RootState) => state.auth?.central?.api_token);

  const handleCreate = async () => {
    try {
      const res = await createBackup({ label: "manual" }).unwrap();
      Swal.fire("Succès", res.message, "success");
    } catch (err: any) {
      Swal.fire("Erreur", err?.data?.message || "mongodump n'est pas installé sur le serveur", "error");
    }
  };

  const handleDelete = async (name: string) => {
    const result = await Swal.fire({
      title: `Supprimer ${name} ?`,
      text: "Cette action est irréversible",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Supprimer",
      cancelButtonText: "Annuler",
    });
    if (result.isConfirmed) {
      try {
        await deleteBackup(name).unwrap();
        Swal.fire("Supprimé", "Backup supprimé", "success");
      } catch (err: any) {
        Swal.fire("Erreur", err?.data?.message || "Erreur", "error");
      }
    }
  };

  const handleDownload = (name: string) => {
    const url = `${process.env.REACT_APP_BASE_URL}/api/backups/${name}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => res.blob())
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      });
  };

  const getTypeBadge = (name: string) => {
    if (name.startsWith("cloture_")) return <Badge bg="warning">Clôture</Badge>;
    if (name.startsWith("daily_")) return <Badge bg="info">Automatique</Badge>;
    return <Badge bg="secondary">Manuel</Badge>;
  };

  return (
    <Card>
      <Card.Header className="d-flex align-items-center">
        <h5 className="card-title mb-0 flex-grow-1">
          <i className="ri-hard-drive-2-line me-2"></i>
          Sauvegardes de la base de données
        </h5>
        <Button variant="primary" size="sm" onClick={handleCreate} disabled={creating}>
          {creating ? (
            <Spinner animation="border" size="sm" className="me-1" />
          ) : (
            <i className="ri-add-line me-1"></i>
          )}
          Nouveau backup
        </Button>
      </Card.Header>
      <Card.Body>
        <div className="alert alert-info d-flex align-items-start mb-4">
          <i className="ri-information-line me-2 mt-1"></i>
          <div>
            <strong>Politique de rétention :</strong> Les backups journaliers sont conservés 30 jours.
            Les backups de clôture sont conservés 2 ans. Les backups manuels ne sont jamais supprimés automatiquement.
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-4"><Spinner animation="border" /></div>
        ) : backups.length === 0 ? (
          <div className="text-center py-5">
            <i className="ri-hard-drive-2-line text-muted" style={{ fontSize: "3rem" }}></i>
            <p className="text-muted mt-3">Aucun backup disponible</p>
            <Button variant="primary" onClick={handleCreate}>
              Créer le premier backup
            </Button>
          </div>
        ) : (
          <Table className="table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Type</th>
                <th>Nom du fichier</th>
                <th>Taille</th>
                <th>Date</th>
                <th style={{ width: 100 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {backups.map((b) => (
                <tr key={b.name}>
                  <td>{getTypeBadge(b.name)}</td>
                  <td className="fw-medium">{b.name}</td>
                  <td>{b.sizeFormatted}</td>
                  <td>{new Date(b.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</td>
                  <td>
                    <div className="d-flex gap-1">
                      <Button variant="soft-primary" size="sm" onClick={() => handleDownload(b.name)} title="Télécharger">
                        <i className="ri-download-2-line"></i>
                      </Button>
                      <Button variant="soft-danger" size="sm" onClick={() => handleDelete(b.name)} title="Supprimer">
                        <i className="ri-delete-bin-line"></i>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
};

export default BackupsTab;
